import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  defaultPaymentFrequency,
  genderOptions,
  maritalStatusOption,
  paymentModels,
  titleOptions,
} from '@nxsys/constants/AppConstants';
import { useExportAllEmployee, useExportEmployee } from '@nxsys/hooks/api';
import useExportAllSocket from '@nxsys/hooks/useExportAllSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { countryList, getAppConfig, getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { filter, find, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const EnrollmentErrorModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const { messages } = useTypedIntl();
  const paymentModelOptions = getAppConfig('paymentModels');
  const dispatch = useDispatch();
  const { user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  // Country-only: this modal is reachable from PensionContribution.tsx,
  // which DashboardUmbrella's admin role also renders, so `isBureau &&`
  // mislabeled the CSV column "NI Number" for an umbrella-Ireland tenant.
  const isIreland = payrollCountry === IRELAND;
  const { employeeList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { list, pagination, loading, queryParams } = employeeList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<EmployeeRecordType[]>([]);

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetEmployeeListParams) => {
    const newParams = {
      ...params,
      pensionsError: 'TRUE',
      isPendingSubmitPension: true,
      pensionErrorType: 'SMART_PENSION_ENROLLMENT',
      reportType: 'pension',
      paymentModels: paymentModelOptions?.map((item) => item.value),
      taxYear: currentTaxYear,
    };
    dispatch(
      employeeActions.getEmployeeList(newParams as GetEmployeeListParams),
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const { run: exportEmployee, loading: isExportEmployeeLoading } =
    useExportEmployee();
  const handleExportErrors = (data: EmployeeRecordType[]) => {
    const dataExport = data.map((item) => {
      const {
        title,
        gender,
        DOB,
        agencyRef,
        forename,
        surname,
        mobile,
        email,
        address,
        city,
        county,
        country,
        postCode,
        maritalStatus,
        NINumber,
        nationality,
        prospectOnboarding,
        primaryAgency,
        errorUpdatingSmartPension,
        errorLeaveSmartPension,
        errorSubmittingSmartPension,
      } = item;
      return {
        id: item.id || '',
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${agencyRef || NINumber || item.id || ''
          }`,
        isOnboarding: prospectOnboarding ? 'Yes' : 'No',
        title: title
          ? find(titleOptions, (t) => t.value === title)?.label || ''
          : '',
        forename: forename || '',
        surname: surname || '',
        DOB: DOB ? moment(DOB).format('DD/MM/YYYY') : '',
        gender: gender
          ? find(genderOptions, (t) => t.value === gender)?.label || ''
          : '',
        maritalStatus: maritalStatus
          ? find(maritalStatusOption, (m) => m.value === maritalStatus)
            ?.label || ''
          : '',
        NINumber: NINumber || '',
        nationality: nationality || '',
        address1: !isEmpty(address) ? address[0] || '' : '',
        address2: !isEmpty(address) ? address[1] || '' : '',
        address3: !isEmpty(address) ? address[2] || '' : '',
        city: city || '',
        county: county || '',
        postCode: postCode || '',
        country: country
          ? find(countryList, (item) => item?.value === country)?.label
          : '',
        mobile: mobile || '',
        email: email || '',
        errorUpdatingSmartPension: errorUpdatingSmartPension || '',
        errorLeaveSmartPension: errorLeaveSmartPension || '',
        errorSubmittingSmartPension: errorSubmittingSmartPension || '',
      };
    });

    const headers = [
      'Employee ID',
      'Agency Ref',
      'Onboarding Required',
      'Title',
      'Forename',
      'Surname',
      'DOB',
      'Gender',
      'Marital Status',
      isIreland ? 'PPS Number' : 'NI Number',
      'Nationality',
      'Adress1',
      'Address2',
      'Address3',
      'City/Town',
      'County',
      'Post Code',
      'Country',
      'Mobile',
      'Email',
      'Error Updating Smart Pension',
      'Error Leave Smart Pension',
      'Error Submitting Smart Pension',
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `Pension-Enrollment-report-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}` || 'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };
  const handleExportErrorsSelected = async () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    const ids = selectedRows.map((item) => item.id);
    const res = await exportEmployee({
      page: 0,
      size: 20,
      ids,
    });
    handleExportErrors(res.data);
  };

  useExportAllSocket({
    eventName: `export-all-process-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        window.open(data.link, '_blank');
        showMessage({
          type: 'success',
          content: messages['common.exportAllSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: data?.error?.message || 'System Error',
        });
      }
    },
  });
  const { run: exportAllEmployee, loading: isExportAllEmployeeLoading } =
    useExportAllEmployee();
  const handleExportAllErrors = () => {
    exportAllEmployee({
      ...queryParams,
      size: pagination.totalDocs,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['common.exportAllPending'],
      });
    });
  };

  const columns: ColumnsType<EmployeeRecordType> = [
    {
      title: 'Error',
      render: (value) =>
        value?.errorUpdatingSmartPension ||
        value?.errorSubmittingSmartPension ||
        value?.errorLeaveSmartPension ||
        '_',
    },
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'name',
      title: 'Name',
      render: (value) =>
        value?.fullName ||
        `${value?.forename || ''} ${value?.surname || ''}` ||
        '_',
      width: 300,
    },
    {
      title: 'Agency Ref',
      width: 250,
      render: (record) =>
        `${record?.primaryAgency?.agencyPrefix || '___'}/${record?.agencyRef || record?.systemId || '_'
        }`,
    },
    {
      key: 'companyPayment',
      title: 'Payment Company',
      dataIndex: 'companyPayment',
      width: 300,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'paymentModel',
      title: 'Payment Model',
      dataIndex: 'setting',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        paymentModels[value?.paymentModel as keyof typeof paymentModels] || 'Not Set',
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
          filter(defaultPaymentFrequency, (item) => item?.value === value)
          ? filter(defaultPaymentFrequency, (item) => item?.value === value)[0]
            ?.label
          : '',
    },
    {
      key: 'primaryAgency',
      title: 'Primary Agency',
      dataIndex: 'primaryAgency',
      width: 350,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.name || '_',
    },
    {
      key: 'mobileNo',
      title: 'Mobile No',
      dataIndex: 'mobile',
    },
    {
      key: 'emailID',
      title: 'Email ID',
      dataIndex: 'email',
      width: 300,
    },
  ];

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className='whitespace-nowrap'>Enrollment Error</div>
          <div className="flex justify-end flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4 items-end">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'exportErrors',
                      label: 'Export Errors',
                      onClick: handleExportErrorsSelected,
                    },
                    {
                      key: 'exportAllErrors',
                      label: 'Export All Errors',
                      onClick: handleExportAllErrors,
                    },
                  ],
                }}
              >
                <AppButton
                  name="Actions"
                  rightIcon={<DownOutlined />}
                  loading={isExportEmployeeLoading || isExportAllEmployeeLoading}
                  disabled={isExportEmployeeLoading || isExportAllEmployeeLoading}
                />
              </Dropdown>
            </div>
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default EnrollmentErrorModal;
